import * as React from 'react';

import {
  Col,
  Popover,
  PopoverTrigger,
  Row,
  SearchField,
  cn,
  scrollIntoView,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideDot, LucideLoader2 } from 'lucide-react';

import { ObjectListEntry } from '../_state/_types';
import * as conversationState from '../_state/conversation.state';
import * as objectsState from '../_state/objects.state';

import { withStatusColorStyle } from './withStatusColorStyle';

export type ObjectRowProps = {
  object: ObjectListEntry;
  onClick: (object: ObjectListEntry) => void;
  preventHovering?: boolean;
  focused?: boolean;
  onMouseEnter: () => void;
};

export const ObjectRow = ({
  object,
  onClick,
  preventHovering,
  focused,
  onMouseEnter,
}: ObjectRowProps) => {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (focused) {
      scrollIntoView({ element: ref.current, gutter: 4 });
    }
  }, [focused]);

  return (
    <Col
      ref={ref}
      style={withStatusColorStyle(object.status)}
      className={cn(
        '@ObjectRow cursor-pointer rounded px-3 py-1 text-sm',
        !preventHovering && 'hover:bg-neutral/7',
        focused && 'bg-neutral/7',
      )}
      onClick={() => onClick(object)}
      onMouseDown={(e) => {
        e.preventDefault();
      }}
      onMouseEnter={onMouseEnter}
    >
      <Row>
        <span className={'font-semibold'}>{object.title}</span>
        <LucideDot className={'size-4 opacity-40'} />
        <span className={'text-custom-color'}>{object.status}</span>
      </Row>
      <Row className={'w-full text-current/50'}>{object.subtitle || '–'}</Row>
    </Col>
  );
};

export type NewObjectPopoverProps = { children: React.ReactNode };

export const NewObjectPopover = (props: NewObjectPopoverProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [focusedIndex, setFocusedIndex] = React.useState(-1);

  const [searchTerm, setSearchTerm] = useAtom(
    objectsState.$objectsSearchTerm.value,
  );

  const isFetchingObjects = useAtomValue(objectsState.$isFetchingObjects);
  const isLoadingObjects = useAtomValue(objectsState.$isLoadingObjects);
  const addObject = useSetAtom(conversationState.$addObject);

  const [preventHovering, setPreventHovering] = React.useState(false);

  const filteredObjectList = useAtomValue(objectsState.$filteredObjectList);

  const handleObjectClick = React.useCallback(
    (object: ObjectListEntry) => {
      setIsOpen(false);
      setSearchTerm('');
      setFocusedIndex(-1);
      addObject(object);
    },
    [addObject, setSearchTerm],
  );

  const handleSearchKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      setPreventHovering(true);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex(
            Math.min(focusedIndex + 1, (filteredObjectList?.length ?? 0) - 1),
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex(Math.max(focusedIndex - 1, 0));
          break;

        case 'Enter':
          e.preventDefault();
          if (focusedIndex >= 0) {
            const object = filteredObjectList?.[focusedIndex];
            if (object) {
              handleObjectClick(object);
            }
          }
          break;
      }
    },
    [filteredObjectList, focusedIndex, handleObjectClick],
  );

  React.useEffect(() => {
    setFocusedIndex(0);
  }, [filteredObjectList]);

  return (
    <PopoverTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      {props.children}
      <Popover>
        <Col className={'w-[300px]'}>
          <div className={'border-b border-neutral/20 p-3'}>
            <SearchField
              placeholder="Search contact"
              autoFocus
              value={searchTerm}
              onChange={(value) => setSearchTerm(value)}
              className={cn(isFetchingObjects && '[&_svg]:animate-spin')}
              icon={isFetchingObjects ? <LucideLoader2 /> : undefined}
              onKeyDown={handleSearchKeyDown}
            />
          </div>
          <Col
            className={'max-h-[300px] overflow-auto p-1'}
            onMouseMove={() => {
              setPreventHovering(false);
            }}
          >
            {filteredObjectList?.map((object, index) => (
              <ObjectRow
                key={object.id}
                object={object}
                focused={focusedIndex === index}
                onClick={handleObjectClick}
                preventHovering={preventHovering}
                onMouseEnter={() => setFocusedIndex(index)}
              />
            ))}
            {!isLoadingObjects && !filteredObjectList?.length && (
              <div className={'p-3 text-center text-sm text-current/50'}>
                No objects found
              </div>
            )}
          </Col>
        </Col>
      </Popover>
    </PopoverTrigger>
  );
};
